import React, { useEffect, useState, useRef } from 'react';
import { theme } from '../theme';
import { Card, DotLoading } from 'antd-mobile';
import { Trophy, Medal, Star } from 'lucide-react';
import { useStore } from '../store/useStore';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://porddee-api.lungnuek.workers.dev/api';

interface Streak {
    startMs: number;
    endMs: number | null;
    durationMs: number;
}

const f2 = (n: number) => String(n).padStart(2, '0');

const msToTime = (ms: number) => {
    const total = Math.floor(ms / 1000);
    const days = Math.floor(total / 86400);
    const hrs = Math.floor((total % 86400) / 3600);
    const mins = Math.floor((total % 3600) / 60);
    const secs = total % 60;
    return { days, hrs, mins, secs };
};

const formatDate = (ms: number) =>
    new Date(ms).toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: '2-digit' });

const MEDALS = ['🥇', '🥈', '🥉', '4️⃣', '5️⃣'];

export const Explore: React.FC = () => {
    const { user } = useStore();
    const [streaks, setStreaks] = useState<Streak[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [tick, setTick] = useState(0);
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

    useEffect(() => {
        window.scrollTo(0, 0);
        document.title = 'สถิติของฉัน';
        return () => { document.title = 'porddee - ตัวช่วยเลิกสูบ'; };
    }, []);

    useEffect(() => {
        if (!user?.id) return;
        setIsLoading(true);
        fetch(`${API_BASE_URL}/stats/${user.id}/streak`)
            .then(r => r.json())
            .then(data => {
                const parsed: Streak[] = (data.topStreaks || []).map((s: any) => ({
                    startMs: Number(s.startMs),
                    endMs: s.endMs != null ? Number(s.endMs) : null,
                    durationMs: Number(s.durationMs),
                }));
                setStreaks(parsed);
            })
            .catch(e => console.error('streak fetch error', e))
            .finally(() => setIsLoading(false));
    }, [user?.id]);

    // Tick every second
    useEffect(() => {
        timerRef.current = setInterval(() => setTick(t => t + 1), 1000);
        return () => { if (timerRef.current) clearInterval(timerRef.current); };
    }, []);
    void tick; // used to trigger re-render

    const now = Date.now();
    const computed = streaks
        .map(s => ({
            ...s,
            liveDuration: s.endMs === null ? Math.max(0, now - s.startMs) : s.durationMs,
        }))
        .sort((a, b) => b.liveDuration - a.liveDuration)
        .slice(0, 5);

    const best = computed[0] ?? null;
    const bestTime = best ? msToTime(best.liveDuration) : null;
    const isLive = best?.endMs === null;

    return (
        <div style={{
            minHeight: '100dvh',
            background: '#f8fafc',
            display: 'flex',
            flexDirection: 'column',
            fontFamily: '"Prompt", sans-serif',
            paddingBottom: 'calc(env(safe-area-inset-bottom) + 80px)',
        }}>
            <div style={{ padding: 20, flex: 1 }}>

                <h2 style={{ fontSize: 22, fontWeight: 900, color: theme.primary, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Trophy size={22} color="#eab308" /> สถิติที่ดีที่สุด
                </h2>
                <p style={{ color: '#64748b', fontSize: 13, marginBottom: 20, lineHeight: 1.6 }}>
                    เวลานานสุดที่คุณเอาชนะใจตัวเองได้ ลองทำลายสถิตินี้!
                </p>

                {isLoading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
                        <DotLoading color={theme.primary} style={{ fontSize: 40 }} />
                    </div>
                ) : !best ? (
                    <div style={{ textAlign: 'center', padding: '60px 20px', color: '#94a3b8', fontSize: 14 }}>
                        ยังไม่มีข้อมูล เริ่มบันทึกเพื่อดูสถิติได้เลย!
                    </div>
                ) : (
                    <>
                        {/* ── #1 Big Best Streak Card ── */}
                        <Card style={{ borderRadius: 24, border: 'none', boxShadow: '0 10px 40px rgba(0,0,0,0.12)', marginBottom: 12, overflow: 'hidden', padding: 0 }}>
                            <div style={{ padding: 'clamp(16px, 5vw, 24px)', background: 'linear-gradient(135deg, #1e293b, #0f172a)' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                                    <div style={{ width: 46, height: 46, borderRadius: 14, background: 'rgba(255,255,255,0.1)', color: '#fcd34d', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                        <Medal size={22} />
                                    </div>
                                    <div>
                                        <div style={{ fontSize: 'clamp(13px, 4vw, 14px)', fontWeight: 700, color: 'white' }}>เวลาที่นานที่สุดที่ไม่ได้สูบ</div>
                                        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                                            {formatDate(best.startMs)} – {best.endMs ? formatDate(best.endMs) : 'ปัจจุบัน'}
                                            {isLive && (
                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#4ade80', fontWeight: 600, marginLeft: 4 }}>
                                                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#4ade80', display: 'inline-block' }} />
                                                    กำลังนับ
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 18, padding: 'clamp(12px, 4vw, 20px) clamp(8px, 2vw, 12px)' }}>
                                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 'clamp(4px, 1.5vw, 8px)' }}>
                                        {[
                                            { val: bestTime!.days, label: 'วัน', color: '#fcd34d', size: 'clamp(32px, 10vw, 52px)' },
                                            { val: bestTime!.hrs, label: 'ชม.', color: '#facc15', size: 'clamp(24px, 7vw, 40px)' },
                                            { val: bestTime!.mins, label: 'นาที', color: '#fde047', size: 'clamp(24px, 7vw, 40px)' },
                                            { val: bestTime!.secs, label: 'วิ.', color: '#fef08a', size: 'clamp(22px, 6vw, 36px)' },
                                        ].map((item, i) => (
                                            <React.Fragment key={i}>
                                                {i > 0 && <div style={{ fontSize: 'clamp(16px, 4vw, 22px)', color: 'rgba(255,255,255,0.2)', marginBottom: 'clamp(10px, 3vw, 18px)' }}>:</div>}
                                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 'clamp(36px, 8vw, 44px)' }}>
                                                    <div style={{ fontSize: item.size, fontWeight: 900, color: item.color, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                                                        {i === 0 ? item.val : f2(item.val)}
                                                    </div>
                                                    <div style={{ fontSize: 'clamp(10px, 2.5vw, 11px)', fontWeight: 600, color: 'rgba(255,255,255,0.6)', marginTop: 4 }}>{item.label}</div>
                                                </div>
                                            </React.Fragment>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </Card>

                        {/* Congrats banner */}
                        {bestTime!.days >= 1 && (
                            <Card style={{ borderRadius: 18, border: 'none', boxShadow: '0 4px 16px rgba(0,0,0,0.06)', overflow: 'hidden', padding: 0, marginBottom: 16 }}>
                                <div style={{ padding: '14px 18px', background: 'linear-gradient(135deg, #10b981, #059669)', display: 'flex', alignItems: 'center', gap: 12 }}>
                                    <Star size={26} color="#fef08a" fill="#fef08a" />
                                    <div>
                                        <div style={{ fontSize: 14, fontWeight: 800, color: 'white' }}>ยอดเยี่ยมมาก! 🎉</div>
                                        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.9)' }}>คุณเคยหยุดได้เป็นวันแล้ว ลองทำลายสถิตินี้!</div>
                                    </div>
                                </div>
                            </Card>
                        )}

                        {/* ── Top 5 Leaderboard ── */}
                        <h3 style={{ fontSize: 15, fontWeight: 800, color: '#0f172a', marginBottom: 12, marginTop: 4 }}>
                            🏆 5 อันดับที่หยุดสูบได้นานที่สุด
                        </h3>

                        {/* Filled ranks */}
                        {computed.map((streak, idx) => {
                            const t = msToTime(streak.liveDuration);
                            const isCurrent = streak.endMs === null;
                            return (
                                <Card key={idx} style={{
                                    borderRadius: 16,
                                    border: idx === 0 ? `1.5px solid ${theme.primary}33` : '1.5px solid #f1f5f9',
                                    boxShadow: 'none', marginBottom: 10, padding: 0, overflow: 'hidden',
                                    background: idx === 0 ? `linear-gradient(135deg, ${theme.primary}0a, white)` : 'white',
                                }}>
                                    <div style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 14 }}>
                                        <div style={{ fontSize: 24, lineHeight: 1, minWidth: 28, textAlign: 'center' }}>{MEDALS[idx]}</div>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: 'clamp(13px, 4vw, 15px)', fontWeight: 800, color: '#0f172a', fontVariantNumeric: 'tabular-nums' }}>
                                                {t.days > 0 ? `${t.days} วัน ` : ''}
                                                {f2(t.hrs)} ชม. {f2(t.mins)} นาที
                                                {idx === 0 ? ` ${f2(t.secs)} วิ.` : ''}
                                            </div>
                                            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2, display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                                                {formatDate(streak.startMs)} – {streak.endMs ? formatDate(streak.endMs) : 'ปัจจุบัน'}
                                                {isCurrent && (
                                                    <span style={{ color: '#10b981', fontWeight: 700 }}>▶ กำลังนับ</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </Card>
                            );
                        })}

                        {/* Empty placeholder slots */}
                        {Array.from({ length: Math.max(0, 5 - computed.length) }).map((_, i) => {
                            const rank = computed.length + i;
                            return (
                                <Card key={`empty-${i}`} style={{ borderRadius: 16, border: '1.5px dashed #e2e8f0', boxShadow: 'none', marginBottom: 10, padding: 0, overflow: 'hidden', opacity: 0.5 }}>
                                    <div style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 14 }}>
                                        <div style={{ fontSize: 24, lineHeight: 1, minWidth: 28, textAlign: 'center' }}>{MEDALS[rank]}</div>
                                        <div style={{ fontSize: 13, color: '#cbd5e1', fontStyle: 'italic' }}>
                                            บันทึกการสูบเพิ่มเพื่อปลดล็อกอันดับนี้
                                        </div>
                                    </div>
                                </Card>
                            );
                        })}
                    </>
                )}
            </div>
        </div>
    );
};
